import type { GeneratePageToolResult } from "schemas/generated/chatMessage";
import type { FileWithLatestFileVersion } from "schemas/generated/file";
import type { GeneratePageToolMetadata } from "schemas/generated/generatePageTool";

import React, { useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { useRealtimeRun } from "@trigger.dev/react-hooks";
import { useUpdateMessageInDBAndStore } from "@v2/features/chat/hooks/useUpdateMessageInDBAndStore";
import { useUpdatePageId } from "@v2/features/chat/hooks/useUpdatePageId";
import { useChatStoreActions } from "@v2/features/chat/store/ChatStoreContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { chatRouter, chatUtils } from "@v2/trpc/routes/chat";
import { fileVersionUtils } from "@v2/trpc/routes/fileVersion";
import { nextJSProjectUtils } from "@v2/trpc/routes/nextJSProject";
import { isDevelopment } from "@v2/utils/env";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import { ArrowUpRight } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import CanceledCard from "./CanceledCard";
import ErrorCard from "./ErrorCard";
import ProgressCard from "./ProgressCard";

function PageGenerationDebug({
  children,
  toolResult,
}: React.PropsWithChildren<{
  toolResult: GeneratePageToolResult;
}>) {
  const runId = toolResult.output.value.jobId;
  const triggerLink = `https://cloud.trigger.dev/orgs/replo-7289/projects/test-quaR/env/${isDevelopment ? "dev" : "prod"}/runs/${runId}`;

  return (
    <div className="flex flex-col gap-2">
      {children}
      {shouldShowV2Debuggers() && runId && (
        <div className="flex flex-wrap gap-2 mt-2">
          <Button
            variant="primary"
            onClick={() => {
              window.open(triggerLink, "_blank");
            }}
            endEnhancer={<ArrowUpRight />}
          >
            Trigger Job
          </Button>
        </div>
      )}
    </div>
  );
}

function usePageGenerationRun(
  toolResult: GeneratePageToolResult,
  toolMessageId?: string,
) {
  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const [jobCompleted, setJobCompleted] = useState(false);
  const { addActiveTriggerJob, removeActiveTriggerJob } = useChatStoreActions();
  const { updatePageId } = useUpdatePageId();

  const jobId = toolResult.output.value.jobId;
  const publicAccessToken = toolResult.output.value.publicAccessToken;
  const updateMessageInDBAndStore = useUpdateMessageInDBAndStore();

  React.useEffect(() => {
    if (chatId && jobId && !jobCompleted) {
      addActiveTriggerJob(chatId, {
        jobId,
        jobType: "generate-page-tool",
      });
    }
  }, [jobId, chatId, jobCompleted, addActiveTriggerJob]);

  const { mutateAsync: updateChatPageId } =
    chatRouter.updateChatPageId.useMutation({
      onSuccess: () => {
        void chatUtils.getPaginatedChats.invalidate({
          projectId,
        });
      },
    });

  async function handleGeneratePageSuccess(
    createdFile: FileWithLatestFileVersion,
  ) {
    void nextJSProjectUtils.listPages.invalidate({ projectId });
    void fileVersionUtils.findByFileId.invalidate({
      fileId: createdFile.id,
    });

    // Automatically switch to the created page in preview panel
    updatePageId(createdFile.id);

    // Update the message to mark as completed with created page ID
    if (toolMessageId) {
      const toolResultContent = [
        {
          type: "tool-result" as const,
          toolCallId: toolResult.toolCallId,
          toolName: toolResult.toolName,
          output: {
            type: "json" as const,
            value: {
              ...toolResult.output.value,
              createdPageId: createdFile.id,
              fileVersionId: createdFile.latestFileVersion?.id,
              isCompleted: true,
            },
          },
          fileId: createdFile.id,
        },
      ];

      updateMessageInDBAndStore(toolMessageId, toolResultContent);

      if (chatId) {
        void updateChatPageId({
          chatId,
          pageId: createdFile.id,
        });
      }
    }
  }

  const { run, error } = useRealtimeRun(jobId, {
    accessToken: publicAccessToken,
    enabled: Boolean(jobId && publicAccessToken && !jobCompleted),
    onComplete: (completedRun) => {
      setJobCompleted(true);

      if (chatId) {
        removeActiveTriggerJob(chatId, completedRun.id);
      }

      if (
        completedRun?.status === "COMPLETED" &&
        completedRun?.output?.createdPage &&
        projectId
      ) {
        void handleGeneratePageSuccess(completedRun.output.createdPage);
      }
    },
  });

  return { run, error };
}

function PageGenerationProgressCard({
  toolResult,
  toolResultMessageId,
}: {
  toolResult: GeneratePageToolResult;
  toolResultMessageId: string;
}) {
  const { run, error } = usePageGenerationRun(toolResult, toolResultMessageId);
  const metadata = run?.metadata as GeneratePageToolMetadata | undefined;
  const updateMessageInDBAndStore = useUpdateMessageInDBAndStore();

  const isFailedRun =
    (run && (run.output?.success === false || Boolean(run.error))) ||
    Boolean(error);
  const isCanceledRun = run && run.status === "CANCELED";

  if (isCanceledRun) {
    const toolResultContent = [
      {
        type: "tool-result" as const,
        toolCallId: toolResult.toolCallId,
        toolName: toolResult.toolName,
        output: {
          type: "json" as const,
          value: {
            ...toolResult.output.value,
            isCanceled: true,
          },
        },
      },
    ];

    updateMessageInDBAndStore(toolResultMessageId, toolResultContent);

    return <CanceledCard />;
  }

  if (isFailedRun) {
    const toolResultContent = [
      {
        type: "tool-result" as const,
        toolCallId: toolResult.toolCallId,
        toolName: toolResult.toolName,
        output: {
          type: "json" as const,
          value: { ...toolResult.output.value, success: false },
        },
      },
    ];

    updateMessageInDBAndStore(toolResultMessageId, toolResultContent);

    return (
      <ErrorCard
        title="We couldn't generate your page"
        subtitle="Something went wrong while generating your page. Please try again or reach out to us at support@replo.com"
      />
    );
  }

  // TODO (Ben, 2025-10-21, REPL-20213): We will want to modify the ProgessCard mapping of these tasks to match this but that will happen with a larger UI refactor
  const metadataProgress = metadata?.tasks?.map((t, i) => ({
    id: (i + 1).toString(),
    headline: t.status === "in_progress" ? t.activeForm : t.content,
    isCompleted: t.status === "completed",
  }));
  const allTasksCompleted = metadataProgress?.every((t) => t.isCompleted);

  const progress = metadataProgress
    ? [
        {
          id: "0",
          headline: "Created tasks",
          isCompleted: true,
        },
        ...metadataProgress,
        {
          id: String(metadataProgress.length + 1),
          headline: allTasksCompleted ? "Creating file" : "Create file",
          isCompleted: false,
        },
      ]
    : [
        {
          id: "0",
          headline: "Creating tasks",
          isCompleted: false,
        },
      ];

  return (
    <ProgressCard
      title={toolResult.output.value.pageName ?? "Generating Page"}
      startedAt={run?.startedAt}
      progress={progress}
    />
  );
}

export default function PageGenerationBlock({
  toolResult,
  toolResultMessageId,
}: {
  toolResult: GeneratePageToolResult | null;
  toolResultMessageId?: string;
}) {
  if (!toolResult || !toolResultMessageId) {
    return <ProgressCard title="Generating Page" />;
  }

  const isFailed =
    !toolResult.output.value.success || toolResult.output.value.error;

  if (isFailed) {
    return (
      <PageGenerationDebug toolResult={toolResult}>
        <ErrorCard
          title="We couldn't generate your page"
          subtitle="Something went wrong while generating your page. Please try again or reach out to us at support@replo.com"
        />
      </PageGenerationDebug>
    );
  }

  const isCanceled = toolResult.output.value.isCanceled;

  if (isCanceled) {
    return (
      <PageGenerationDebug toolResult={toolResult}>
        <CanceledCard />
      </PageGenerationDebug>
    );
  }

  const createdPageId = toolResult.output.value.createdPageId;

  // NOTE (Ben, 2025-11-10, REPL-20213): Once the generation is completed we will use our current ToolSectionCard to display the tools calls made and the version button.
  // Therefore we are hiding this component except in debug mode where we may want to see the tool call info
  if (createdPageId) {
    if (shouldShowV2Debuggers()) {
      return (
        <PageGenerationDebug toolResult={toolResult}>
          DEBUG: generatePageTool
        </PageGenerationDebug>
      );
    }

    return null;
  }

  return (
    <PageGenerationDebug toolResult={toolResult}>
      <PageGenerationProgressCard
        toolResult={toolResult}
        toolResultMessageId={toolResultMessageId}
      />
    </PageGenerationDebug>
  );
}
